import { Box, Stack, Typography } from '@mui/material';
import type { PeriodKey } from '../types';
import { useWidgetData } from '../useWidgetData';
import { formatCurrency } from '../utils';
import { LABEL_COLOR } from '../constants';
import { WidgetCard } from './WidgetCard';

interface OpportunityRangeWidgetProps {
    period: PeriodKey;
    enabled: boolean;
}

const TITLE = 'Opportunity Range';
const SUBTITLE = 'Lower and upper bound by user';

export const OpportunityRangeWidget = ({ period, enabled }: OpportunityRangeWidgetProps) => {
    const { data, isLoading, error } = useWidgetData('opportunityRange', period, enabled);

    if (error) {
        return (
            <WidgetCard
                title={TITLE}
                subtitle={SUBTITLE}
                children={
                    <Typography variant="body2" sx={{ color: '#EF4444' }}>
                        Failed to load opportunity range widget
                    </Typography>
                }
            />
        );
    }

    if (isLoading || !data) {
        return <WidgetCard title={TITLE} subtitle={SUBTITLE} loading />;
    }

    const rows = data.rows;
    const hasRows = rows.length > 0;

    return (
        <WidgetCard
            title={TITLE}
            subtitle={SUBTITLE}
            headline={`${formatCurrency(data.total_min)} – ${formatCurrency(data.total_max)}`}
        >
            {!hasRows ? (
                <Typography variant="body2" sx={{ color: LABEL_COLOR }}>
                    No data for this period
                </Typography>
            ) : (
                <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
                    <Stack spacing={1.5}>
                        {rows.map((row) => (
                            <Box
                                key={row.user_id}
                                sx={{
                                    borderRadius: '8px',
                                    backgroundColor: '#F9FAFB',
                                    p: 1.5,
                                }}
                            >
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="flex-start"
                                    spacing={1}
                                >
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 500, color: '#111827', mb: 0.25 }}
                                        >
                                            {row.user_name}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: LABEL_COLOR }}>
                                            {row.project_count} {row.project_count === 1 ? 'project' : 'projects'}
                                        </Typography>
                                    </Box>
                                    <Stack alignItems="flex-end" spacing={0.25}>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 600, color: '#111827', whiteSpace: 'nowrap' }}
                                        >
                                            {`Min ${formatCurrency(row.min_total)}`}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 600, color: '#111827', whiteSpace: 'nowrap' }}
                                        >
                                            {`Max ${formatCurrency(row.max_total)}`}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Box>
                        ))}
                    </Stack>
                </Box>
            )}
        </WidgetCard>
    );
};

OpportunityRangeWidget.displayName = 'OpportunityRangeWidget';
